import { addDays, format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type AnnouncementDetails,
  AnnouncementStatus,
  type AnnouncementStatusType,
  type CreateAnnouncementPayload,
  useCreateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useUpdateAnnouncementMutation,
} from '~entities/announcements';

import { InputDatePicker } from '~shared/components/input-date-picker';
import { MinimalTiptapEditor } from '~shared/components/minimal-tiptap';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

import {
  AnnouncementActionConfirmDialog,
  type AnnouncementConfirmAction,
} from './announcement-action-confirm-dialog';

interface AnnouncementFormProps {
  announcement?: AnnouncementDetails;
  onSuccess?: () => void;
}

function getDefaultExpiresAtDate() {
  return format(addDays(new Date(), 30), 'yyyy-MM-dd');
}

function stripHtmlToText(html: string) {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function AnnouncementForm({
  announcement,
  onSuccess,
}: AnnouncementFormProps) {
  const { mutateAsync: createAnnouncement, isPending: isCreating } =
    useCreateAnnouncementMutation();
  const { mutateAsync: updateAnnouncement, isPending: isUpdating } =
    useUpdateAnnouncementMutation();
  const { mutateAsync: deleteAnnouncement, isPending: isDeleting } =
    useDeleteAnnouncementMutation();

  const [title, setTitle] = useState(announcement?.title ?? '');
  const [bodyHtml, setBodyHtml] = useState(announcement?.bodyHtml ?? '');
  const [isPinned, setIsPinned] = useState(announcement?.isPinned ?? false);
  const [expiresAt, setExpiresAt] = useState(() => {
    if (announcement) {
      return announcement.expiresAt ? announcement.expiresAt.slice(0, 10) : '';
    }
    return getDefaultExpiresAtDate();
  });
  const [pendingAction, setPendingAction] =
    useState<AnnouncementStatusType | null>(null);
  const [confirmAction, setConfirmAction] =
    useState<AnnouncementConfirmAction | null>(null);

  const isSubmitting = isCreating || isUpdating || isDeleting;

  const submit = async (status: AnnouncementStatusType) => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (status === AnnouncementStatus.PUBLISHED && !stripHtmlToText(bodyHtml)) {
      toast.error('Body is required when publishing');
      return;
    }

    const payload: CreateAnnouncementPayload = {
      title: title.trim(),
      bodyHtml,
      status,
      isPinned,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    };

    setPendingAction(status);
    try {
      if (announcement) {
        await updateAnnouncement({
          id: announcement.id,
          payload,
        });
        toast.success(
          status === AnnouncementStatus.ARCHIVED
            ? 'Announcement archived'
            : 'Announcement updated',
        );
      } else {
        await createAnnouncement(payload);
        toast.success(
          status === AnnouncementStatus.PUBLISHED
            ? 'Announcement published'
            : 'Draft saved',
        );
      }
      onSuccess?.();
    } catch {
      // Error toast handled in mutation
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!announcement) return;

    try {
      await deleteAnnouncement(announcement.id);
      toast.success('Announcement deleted');
      onSuccess?.();
    } catch {
      // Error toast handled in mutation
    } finally {
      setConfirmAction(null);
    }
  };

  return (
    <>
      <Card>
        <CardContent className='space-y-6 pt-6'>
          <div className='space-y-2'>
            <Label htmlFor='announcement-title'>Title</Label>
            <Input
              id='announcement-title'
              value={title}
              maxLength={200}
              placeholder='e.g. Studio closed Monday for holiday'
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />
          </div>

          <div className='space-y-2'>
            <Label>Message</Label>
            <MinimalTiptapEditor
              key={announcement?.id ?? 'new'}
              output='html'
              value={bodyHtml}
              showImageUpload={false}
              placeholder='Write your announcement for families...'
              className='min-h-[240px] rounded-md border'
              editorContentClassName='min-h-[180px] px-3 py-2'
              onChange={(value) => {
                setBodyHtml(typeof value === 'string' ? value : '');
              }}
            />
          </div>

          <div className='flex items-center gap-2'>
            <Checkbox
              id='announcement-pinned'
              checked={isPinned}
              onCheckedChange={(checked) => {
                setIsPinned(checked === true);
              }}
            />
            <Label htmlFor='announcement-pinned'>
              Pin to top of parent feed
            </Label>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='announcement-expires'>Expires on</Label>
            <InputDatePicker
              id='announcement-expires'
              value={expiresAt}
              defaultValue={getDefaultExpiresAtDate()}
              placeholder='No expiration date'
              disablePast
              onChange={setExpiresAt}
            />
            <p className='text-sm text-muted-foreground'>
              Defaults to 30 days from today. Clear to keep the announcement
              visible indefinitely.
            </p>
          </div>

          <div className='flex flex-wrap gap-3'>
            {(!announcement ||
              announcement.status === AnnouncementStatus.DRAFT) && (
              <Button
                type='button'
                variant='secondary'
                disabled={isSubmitting}
                onClick={() => void submit(AnnouncementStatus.DRAFT)}
              >
                {pendingAction === AnnouncementStatus.DRAFT && (
                  <Loader2 className='animate-spin' />
                )}
                Save draft
              </Button>
            )}
            <Button
              type='button'
              className='bg-green-500 text-white hover:bg-green-600'
              disabled={isSubmitting}
              onClick={() => void submit(AnnouncementStatus.PUBLISHED)}
            >
              {pendingAction === AnnouncementStatus.PUBLISHED && (
                <Loader2 className='animate-spin' />
              )}
              Publish
            </Button>
            {announcement && announcement.status !== AnnouncementStatus.ARCHIVED && (
              <Button
                type='button'
                className=''
                disabled={isSubmitting}
                onClick={() => void submit(AnnouncementStatus.ARCHIVED)}
              >
                {pendingAction === AnnouncementStatus.ARCHIVED && (
                  <Loader2 className='animate-spin' />
                )}
                Archive
              </Button>
            )}
            {announcement && (
              <Button
                type='button'
                variant='destructive'
                disabled={isSubmitting}
                onClick={() => {
                  setConfirmAction({ type: 'delete', announcement });
                }}
              >
                Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <AnnouncementActionConfirmDialog
        confirmAction={confirmAction}
        isUpdating={isDeleting}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setConfirmAction(null);
          }
        }}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </>
  );
}
